<template>
  <div class="customer-feedback-management">
    <div class="page-header">
      <h2>顾客反馈</h2>
      <p>查看顾客提交的问题与建议，并记录门店处理回复</p>
    </div>

    <div class="page-content">
      <div class="search-section">
        <div class="search-box">
          <select v-model="searchStoreId" class="store-select">
            <option value="">全部门店</option>
            <option v-for="store in storeList" :key="store.id" :value="store.id">{{ store.storeName }}</option>
          </select>
          <select v-model="status" class="store-select">
            <option value="">全部状态</option>
            <option value="PENDING">待处理</option>
            <option value="PROCESSING">处理中</option>
            <option value="RESOLVED">已解决</option>
            <option value="CLOSED">已关闭</option>
          </select>
          <select v-model="feedbackType" class="store-select">
            <option value="">全部类型</option>
            <option value="SERVICE">服务</option>
            <option value="DEVICE">设备</option>
            <option value="RECHARGE">充值</option>
            <option value="CONSUMPTION">消费</option>
            <option value="SUGGESTION">建议</option>
            <option value="OTHER">其他</option>
          </select>
          <input v-model.trim="keyword" class="search-input" placeholder="搜索标题/内容/顾客/手机号" @keyup.enter="loadFeedback" />
          <button class="search-btn" @click="loadFeedback">搜索</button>
          <button class="reset-btn" @click="handleReset">重置</button>
        </div>
      </div>

      <div class="summary-grid">
        <div class="summary-card">
          <span>当前列表</span>
          <strong>{{ totalElements }}</strong>
        </div>
        <div class="summary-card warning">
          <span>待处理</span>
          <strong>{{ pendingCount }}</strong>
        </div>
        <div class="summary-card success">
          <span>已加载</span>
          <strong>{{ feedbackList.length }}</strong>
        </div>
      </div>

      <div class="table-section">
        <div class="table-header">
          <h3>反馈列表</h3>
        </div>
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>顾客</th>
                <th>门店</th>
                <th>类型</th>
                <th>标题</th>
                <th>状态</th>
                <th>提交时间</th>
                <th>处理人</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="8" class="empty-cell">加载中...</td>
              </tr>
              <tr v-else-if="feedbackList.length === 0">
                <td colspan="8" class="empty-cell">暂无顾客反馈</td>
              </tr>
              <tr v-else v-for="item in feedbackList" :key="item.id">
                <td>
                  <div>{{ item.customerName || '-' }}</div>
                  <small>{{ item.customerPhone || item.contact || '-' }}</small>
                </td>
                <td>{{ item.storeName || getStoreName(item.storeId) }}</td>
                <td>{{ typeText(item.feedbackType) }}</td>
                <td class="title-cell">{{ item.title || '-' }}</td>
                <td><span :class="['status-badge', statusClass(item.status)]">{{ statusText(item.status) }}</span></td>
                <td>{{ formatDate(item.createdTime) }}</td>
                <td>{{ item.handlerName || '-' }}</td>
                <td>
                  <div class="action-buttons">
                    <button class="view-btn" @click="openDetail(item)">详情</button>
                    <button class="process-btn" v-if="item.status === 'PENDING'" @click="changeStatus(item, 'PROCESSING')">处理中</button>
                    <button class="close-btn" v-if="item.status !== 'CLOSED'" @click="changeStatus(item, 'CLOSED')">关闭</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="pagination-row">
          <button class="reset-btn" :disabled="page <= 0" @click="changePage(page - 1)">上一页</button>
          <span>第 {{ page + 1 }} 页 / 共 {{ totalPages || 1 }} 页</span>
          <button class="reset-btn" :disabled="page + 1 >= totalPages" @click="changePage(page + 1)">下一页</button>
        </div>
      </div>
    </div>

    <div v-if="selectedFeedback" class="modal-overlay" @click.self="closeDetail">
      <div class="detail-modal">
        <div class="modal-header">
          <h3>反馈详情</h3>
          <button class="modal-close-btn" @click="closeDetail">×</button>
        </div>
        <div class="modal-body">
          <div class="detail-grid">
            <div><label>顾客</label><span>{{ selectedFeedback.customerName || '-' }}</span></div>
            <div><label>手机号</label><span>{{ selectedFeedback.customerPhone || selectedFeedback.contact || '-' }}</span></div>
            <div><label>门店</label><span>{{ selectedFeedback.storeName || getStoreName(selectedFeedback.storeId) }}</span></div>
            <div><label>状态</label><span>{{ statusText(selectedFeedback.status) }}</span></div>
          </div>
          <div class="detail-block">
            <label>{{ typeText(selectedFeedback.feedbackType) }} · {{ selectedFeedback.title }}</label>
            <p>{{ selectedFeedback.content }}</p>
          </div>
          <div class="image-list" v-if="imageList(selectedFeedback).length">
            <img v-for="image in imageList(selectedFeedback)" :key="image" :src="image" @click="previewImage(image)" />
          </div>
          <div class="form-group">
            <label>处理回复</label>
            <textarea v-model.trim="replyText" class="reply-input" placeholder="填写处理结果，顾客可在小程序查看"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="reset-btn" @click="closeDetail">取消</button>
          <button class="process-btn" @click="saveReply('PROCESSING')">保存为处理中</button>
          <button class="search-btn" @click="saveReply('RESOLVED')">回复并解决</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'

export default {
  name: 'CustomerFeedbackManagement',
  data() {
    return {
      feedbackList: [],
      storeList: [],
      searchStoreId: '',
      status: '',
      feedbackType: '',
      keyword: '',
      loading: false,
      page: 0,
      size: 20,
      totalElements: 0,
      totalPages: 0,
      pendingCount: 0,
      selectedFeedback: null,
      replyText: ''
    }
  },
  mounted() {
    this.loadStoreList()
    this.loadFeedback()
    this.loadPendingCount()
  },
  methods: {
    async loadStoreList() {
      const response = await request.get('/api/stores/getStores', { params: { page: 0, size: 1000 } })
      if (response.data.success && response.data.code === 200) {
        this.storeList = response.data.data.content || []
      }
    },
    async loadFeedback() {
      this.loading = true
      try {
        const response = await request.get('/api/miniapp/admin/feedback', {
          params: {
            page: this.page,
            size: this.size,
            storeId: this.searchStoreId || undefined,
            status: this.status || undefined,
            type: this.feedbackType || undefined,
            keyword: this.keyword || undefined
          }
        })
        if (response.data.success && response.data.code === 200) {
          const pageData = response.data.data || {}
          this.feedbackList = pageData.content || []
          this.totalElements = pageData.totalElements || 0
          this.totalPages = pageData.totalPages || 0
        }
      } finally {
        this.loading = false
      }
    },
    async loadPendingCount() {
      const response = await request.get('/api/miniapp/admin/feedback/pending-count')
      if (response.data.success && response.data.code === 200) {
        this.pendingCount = Number((response.data.data && response.data.data.count) || 0)
      }
    },
    handleReset() {
      this.searchStoreId = ''
      this.status = ''
      this.feedbackType = ''
      this.keyword = ''
      this.page = 0
      this.loadFeedback()
    },
    changePage(nextPage) {
      if (nextPage < 0 || (this.totalPages && nextPage >= this.totalPages)) return
      this.page = nextPage
      this.loadFeedback()
    },
    async changeStatus(item, status) {
      await request.put(`/api/miniapp/admin/feedback/${item.id}/status`, { status })
      this.loadFeedback()
      this.loadPendingCount()
    },
    openDetail(item) {
      this.selectedFeedback = item
      this.replyText = item.handlerReply || ''
    },
    closeDetail() {
      this.selectedFeedback = null
      this.replyText = ''
    },
    async saveReply(status) {
      if (!this.selectedFeedback) return
      if (!this.replyText) {
        alert('请填写处理回复')
        return
      }
      await request.post(`/api/miniapp/admin/feedback/${this.selectedFeedback.id}/reply`, {
        reply: this.replyText,
        status
      })
      this.closeDetail()
      this.loadFeedback()
      this.loadPendingCount()
    },
    getStoreName(storeId) {
      const store = this.storeList.find(item => String(item.id) === String(storeId))
      return store ? store.storeName : '-'
    },
    imageList(item) {
      return String((item && item.imageUrls) || '')
        .split(',')
        .map(url => this.normalizeImageUrl(url.trim()))
        .filter(Boolean)
    },
    normalizeImageUrl(url) {
      if (!url) return ''
      const value = String(url).replace(/\\/g, '/')
      if (/^data:/i.test(value) || /^(https?:)?\/\//i.test(value)) return value
      if (value.startsWith('/api/')) return value
      if (value.startsWith('/files/')) return `/api${value}`
      if (value.includes('/feedback/')) return `/api/files/feedback/${value.split('/feedback/')[1].replace(/^\/+/, '')}`
      return `/api/files/${value.replace(/^\/+/, '')}`
    },
    previewImage(url) {
      window.open(url, '_blank')
    },
    typeText(type) {
      const labels = {
        SERVICE: '服务',
        DEVICE: '设备',
        RECHARGE: '充值',
        CONSUMPTION: '消费',
        SUGGESTION: '建议',
        OTHER: '其他'
      }
      return labels[type] || '其他'
    },
    statusText(status) {
      const labels = {
        PENDING: '待处理',
        PROCESSING: '处理中',
        RESOLVED: '已解决',
        CLOSED: '已关闭'
      }
      return labels[status] || '待处理'
    },
    statusClass(status) {
      return String(status || 'PENDING').toLowerCase()
    },
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
.page-header { margin-bottom: 24px; }
.page-header h2 { margin: 0 0 8px; color: #333; }
.page-header p { margin: 0; color: #666; }
.page-content, .table-section { background: #fff; border-radius: 8px; padding: 20px; }
.search-section { margin-bottom: 20px; }
.search-box { display: flex; flex-wrap: wrap; gap: 12px; }
.store-select, .search-input { height: 36px; padding: 0 12px; border: 1px solid #d9d9d9; border-radius: 6px; }
.search-input { width: 240px; }
button { border: none; border-radius: 6px; padding: 8px 16px; cursor: pointer; }
button:disabled { cursor: not-allowed; opacity: 0.5; }
.search-btn { background: #1890ff; color: #fff; }
.reset-btn { background: #f5f5f5; color: #333; }
.summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 20px; }
.summary-card { padding: 16px; background: #f7f8fa; border-radius: 8px; }
.summary-card span, .summary-card strong { display: block; }
.summary-card span { color: #666; margin-bottom: 8px; }
.summary-card strong { font-size: 24px; color: #333; }
.summary-card.warning strong { color: #fa8c16; }
.summary-card.success strong { color: #52c41a; }
.table-container { overflow-x: auto; }
.data-table { width: 100%; min-width: 1100px; border-collapse: collapse; }
.data-table th, .data-table td { padding: 12px; border-bottom: 1px solid #f0f0f0; text-align: left; vertical-align: top; }
.data-table small { display: block; margin-top: 4px; color: #999; }
.empty-cell { text-align: center; color: #999; padding: 40px; }
.title-cell { max-width: 260px; }
.status-badge { display: inline-block; padding: 4px 10px; border-radius: 12px; font-size: 12px; font-weight: 600; }
.status-badge.pending { background: #fff7e6; color: #fa8c16; }
.status-badge.processing { background: #e6f7ff; color: #1890ff; }
.status-badge.resolved { background: #f6ffed; color: #52c41a; }
.status-badge.closed { background: #f5f5f5; color: #666; }
.action-buttons { display: flex; flex-wrap: wrap; gap: 8px; }
.view-btn { background: #722ed1; color: #fff; }
.process-btn { background: #13c2c2; color: #fff; }
.close-btn { background: #ff4d4f; color: #fff; }
.pagination-row { display: flex; align-items: center; justify-content: center; gap: 16px; padding-top: 16px; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.detail-modal { width: 760px; max-height: 88vh; overflow-y: auto; background: #fff; border-radius: 10px; }
.modal-header, .modal-footer { display: flex; align-items: center; justify-content: space-between; padding: 18px 22px; border-bottom: 1px solid #f0f0f0; }
.modal-footer { border-top: 1px solid #f0f0f0; border-bottom: 0; justify-content: flex-end; gap: 12px; }
.modal-header h3 { margin: 0; }
.modal-close-btn { background: transparent; font-size: 24px; padding: 0; }
.modal-body { padding: 22px; }
.detail-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; margin-bottom: 18px; }
.detail-grid label, .detail-block label, .form-group label { display: block; margin-bottom: 6px; color: #666; font-size: 13px; }
.detail-grid span { color: #333; font-weight: 600; }
.detail-block { padding: 16px; border-radius: 8px; background: #fafafa; margin-bottom: 18px; }
.detail-block p { margin: 0; line-height: 1.7; color: #333; white-space: pre-wrap; }
.image-list { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 18px; }
.image-list img { width: 120px; height: 120px; object-fit: cover; border-radius: 8px; cursor: pointer; }
.reply-input { width: 100%; min-height: 120px; border: 1px solid #d9d9d9; border-radius: 6px; padding: 10px; box-sizing: border-box; resize: vertical; }
</style>
